/**
 * MilestoneRfiMount — a milestone's linked RFI, mounted inline under MilestoneSections
 * when the milestone's deliverable type is the RFI Response template (isRfiMilestoneType).
 *
 * Mirrors MilestoneAsiMount.jsx exactly (RFIs run the same way as ASIs — see
 * docs/PASSENGERS.md). The reverse direction (RFI -> milestone) is CaMilestoneMount in
 * ConstructionSection.jsx, which renders the canonical MilestoneRow inline under an expanded RFI.
 *
 * Lookup: Supabase reverse query by milestone_airtable_id (getRfiByMilestone), cached in
 * ctx.milestoneRfiCache[msId] as the rfi row or false (no match). The contract record comes
 * from ctx.contractCache (loaded on demand, same as ThreadCaMount/MilestoneAsiMount).
 *
 * Renders null for milestone types that aren't the RFI Response template.
 */

import { useEffect, useRef } from 'react'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { isRfiMilestoneType } from '../outline/constants.js'
import { getRfiByMilestone } from '../services/rfis.js'
import { getProjectContracts } from '../services/bidCampaigns.js'
import { RfiNode } from './ConstructionSection.jsx'

function ensureContractsLoaded(projId) {
  if (!projId) return
  if (ctx.contractCache !== undefined) return
  ctx.contractCache = null
  getProjectContracts(projId).then(c => {
    ctx.contractCache = c
    window.dispatchEvent(new Event('outline-render'))
  }).catch(() => {
    ctx.contractCache = []
    window.dispatchEvent(new Event('outline-render'))
  })
}

export default function MilestoneRfiMount({ ms, projectId }) {
  // The type check takes the Deliverable Type record ID since the milestone-type-ids change; passing
  // the type NAME made this always false, so no linked record ever showed (fixed 2026-09-24).
  const typeName = (ms?.fields?.['Deliverable'] || [])[0] || ''
  const msId = ms?.id || ''
  const isRfiMs = isRfiMilestoneType(typeName)
  const loadedRef = useRef(false)

  function fetchRfi() {
    if (!ctx.milestoneRfiCache) ctx.milestoneRfiCache = {}
    if (ctx.milestoneRfiCache[msId] !== undefined) return
    ctx.milestoneRfiCache[msId] = null // in-flight
    getRfiByMilestone(msId).then(rfi => {
      if (!ctx.milestoneRfiCache) ctx.milestoneRfiCache = {}
      ctx.milestoneRfiCache[msId] = rfi || false
      if (rfi && rfi.project_id) ensureContractsLoaded(rfi.project_id)
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.milestoneRfiCache) ctx.milestoneRfiCache = {}
      ctx.milestoneRfiCache[msId] = false
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  // Lazy-load the linked RFI once per milestone (hooks before any return)
  useEffect(() => {
    if (!isRfiMs || !msId || loadedRef.current) return
    loadedRef.current = true
    fetchRfi()
  }, [isRfiMs, msId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch after a cache bust (e.g. onChanged from an edit made via this mount).
  useEffect(() => {
    if (!isRfiMs || !msId) return
    function onRender() {
      if (!loadedRef.current) return
      if (ctx.milestoneRfiCache?.[msId] === undefined) fetchRfi()
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [isRfiMs, msId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isRfiMs || !msId) return null

  const entry = ctx.milestoneRfiCache?.[msId]
  if (entry === undefined || entry === null) return null // not started / in-flight — quiet
  if (entry === false) return null // no RFI linked to this milestone

  const rfi = entry
  const contracts = Array.isArray(ctx.contractCache) ? ctx.contractCache : []
  const contract = contracts.find(c => c.id === rfi.contract_id)
    || { id: rfi.contract_id, project_id: rfi.project_id || projectId || wf.zoomProjectId || '' }

  function onChanged() {
    if (ctx.milestoneRfiCache) ctx.milestoneRfiCache[msId] = undefined
    if (ctx.rfiCache && rfi.contract_id) ctx.rfiCache[rfi.contract_id] = undefined
    window.dispatchEvent(new Event('outline-render'))
  }

  return <RfiNode rfi={rfi} contract={contract} onChanged={onChanged} hideMilestoneMount labelPrefix="RFI: " mountCtx={'ms:' + msId} />
}
